export const THEME_STORAGE_KEY = "seekee-theme:v1";
export const THEME_EVENT_NAME = "seekee:theme-change";

export type ThemeMode = "light" | "dark";

type ThemeSource = "bootstrap" | "host" | "runtime";

const MAX_THEME_PAYLOAD_DEPTH = 5;
const THEME_RECEIVE_DATA_KEYS = new Set([
  "theme",
  "themeChanged",
  "appThemeChanged",
]);
// 与 globals.css 的 light --theme-surface / dark --theme-page-bg 保持一致。
const THEME_META_COLORS: Record<ThemeMode, string> = {
  light: "#FFFFFF",
  dark: "#0F1115",
};

interface ApplyThemeOptions {
  persist?: boolean;
  source?: ThemeSource;
}

export function normalizeTheme(value: unknown): ThemeMode | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return normalized === "light" || normalized === "dark" ? normalized : null;
}

function readSearchTheme(search: string): ThemeMode | null {
  if (!search) return null;
  const params = new URLSearchParams(
    search.startsWith("?") ? search : `?${search}`
  );
  return normalizeTheme(params.get("theme"));
}

export function readThemeFromLocation(location: Location): ThemeMode | null {
  const directTheme = readSearchTheme(location.search);
  if (directTheme) return directTheme;

  const queryIndex = location.hash.indexOf("?");
  return queryIndex >= 0
    ? readSearchTheme(location.hash.slice(queryIndex + 1))
    : null;
}

function readSystemTheme(targetWindow: Window): ThemeMode {
  return targetWindow.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function readStoredTheme(storage: Storage): ThemeMode | null {
  try {
    return normalizeTheme(storage.getItem(THEME_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function isAppWebView(targetWindow: Window): boolean {
  const userAgent = targetWindow.navigator.userAgent.toLowerCase();
  return (
    userAgent.includes("seekee") ||
    userAgent.includes("buscari") ||
    "quan_function" in targetWindow
  );
}

export function resolveBrowserTheme(targetWindow: Window): ThemeMode {
  const explicitTheme = readThemeFromLocation(targetWindow.location);
  if (explicitTheme) return explicitTheme;

  const storedTheme = readStoredTheme(targetWindow.localStorage);
  if (storedTheme) return storedTheme;

  if (!isAppWebView(targetWindow)) {
    return readSystemTheme(targetWindow);
  }

  return "light";
}

export function extractThemeFromPayload(
  payload: unknown,
  depth = 0
): ThemeMode | null {
  if (depth > MAX_THEME_PAYLOAD_DEPTH) return null;

  const directTheme = normalizeTheme(payload);
  if (directTheme) return directTheme;
  if (!payload || typeof payload !== "object") return null;

  const data = payload as Record<string, unknown>;
  const keyedTheme = data.key === "theme" ? normalizeTheme(data.value) : null;
  if (keyedTheme) return keyedTheme;

  return (
    normalizeTheme(data.theme) ||
    (data.value && typeof data.value === "object"
      ? extractThemeFromPayload(data.value, depth + 1)
      : null) ||
    (data.data && typeof data.data === "object"
      ? extractThemeFromPayload(data.data, depth + 1)
      : null)
  );
}

export function applyTheme(
  theme: ThemeMode,
  { persist = true, source = "runtime" }: ApplyThemeOptions = {}
): ThemeMode {
  if (typeof document === "undefined") return theme;

  const effectiveTheme = theme;
  const root = document.documentElement;
  root.dataset.theme = effectiveTheme;
  root.style.colorScheme = effectiveTheme;

  const themeColor = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]'
  );
  themeColor?.setAttribute("content", THEME_META_COLORS[effectiveTheme]);

  if (persist) {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, effectiveTheme);
    } catch {
      // Storage can be disabled in private browsing or embedded WebViews.
    }
  }

  window.dispatchEvent(
    new CustomEvent(THEME_EVENT_NAME, {
      detail: { theme: effectiveTheme, source },
    })
  );
  return effectiveTheme;
}

export function applyThemeFromPayload(payload: unknown): ThemeMode | null {
  const theme = extractThemeFromPayload(payload);
  return theme ? applyTheme(theme, { source: "host" }) : null;
}

export function applyThemeFromReceiveData(payload: unknown): ThemeMode | null {
  if (!payload || typeof payload !== "object") return null;

  const key = (payload as Record<string, unknown>).key;
  if (typeof key !== "string" || !THEME_RECEIVE_DATA_KEYS.has(key)) {
    return null;
  }

  return applyThemeFromPayload(payload);
}

let syncActiveThemeRoute: (() => void) | null = null;

export function syncThemeRuntimeForRoute(): void {
  syncActiveThemeRoute?.();
}

export function initializeThemeRuntime({
  shouldPersist = () => true,
}: { shouldPersist?: () => boolean } = {}): () => void {
  if (typeof window === "undefined") return () => undefined;

  const syncRoute = (source: ThemeSource = "runtime") => {
    applyTheme(resolveBrowserTheme(window), {
      source,
      persist: shouldPersist(),
    });
  };
  syncActiveThemeRoute = syncRoute;
  syncRoute("bootstrap");

  window.quan = window.quan || {};
  const previousThemeChanged = window.quan.onThemeChanged;
  const themeHandler = (payload: unknown) => applyThemeFromPayload(payload);
  window.quan.onThemeChanged = themeHandler;

  return () => {
    if (syncActiveThemeRoute === syncRoute) syncActiveThemeRoute = null;
    if (previousThemeChanged) window.quan.onThemeChanged = previousThemeChanged;
    else delete window.quan.onThemeChanged;
  };
}

/**
 * Keep this first-paint implementation aligned with normalizeTheme,
 * readThemeFromLocation, readStoredTheme, isAppWebView and resolveBrowserTheme.
 * A rule or precedence change must be applied to both paths to avoid FOUC.
 */
export const THEME_BOOTSTRAP_SCRIPT = `
(function () {
  var storageKey = ${JSON.stringify(THEME_STORAGE_KEY)};
  function normalize(value) {
    if (typeof value !== 'string') return null;
    var normalized = value.trim().toLowerCase();
    return normalized === 'light' || normalized === 'dark' ? normalized : null;
  }
  function readSearch(search) {
    try {
      var params = new URLSearchParams(search && search.charAt(0) === '?' ? search : '?' + (search || ''));
      return normalize(params.get('theme'));
    } catch (_) {
      return null;
    }
  }
  var theme = readSearch(window.location.search);
  if (!theme) {
    var queryIndex = window.location.hash.indexOf('?');
    if (queryIndex >= 0) theme = readSearch(window.location.hash.slice(queryIndex + 1));
  }
  if (!theme) {
    try { theme = normalize(window.localStorage.getItem(storageKey)); } catch (_) {}
  }
  if (!theme) {
    var ua = (window.navigator.userAgent || '').toLowerCase();
    var inApp = ua.indexOf('seekee') >= 0 || ua.indexOf('buscari') >= 0 || 'quan_function' in window;
    theme = !inApp && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
  try { window.localStorage.setItem(storageKey, theme); } catch (_) {}
  var meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '${THEME_META_COLORS.dark}' : '${THEME_META_COLORS.light}');
})();`;
